import { useState, useEffect } from 'react';
import { apiClient, SoakCrashDetail } from '../api/client';

interface CrashModalProps {
  crashId: string;
  onClose: () => void;
}

export function CrashModal({ crashId, onClose }: CrashModalProps) {
  const [crash, setCrash] = useState<SoakCrashDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFullStack, setShowFullStack] = useState(false);

  useEffect(() => {
    const loadCrash = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.getCrashDetails(crashId);
        if (response.success && response.data) {
          setCrash(response.data);
        } else {
          setError(response.error || 'Failed to load crash details');
        }
      } catch {
        setError('Failed to load crash details');
      }
      setIsLoading(false);
    };
    loadCrash();
  }, [crashId]);

  const formatTimestamp = (ts: number): string => {
    return new Date(ts).toLocaleString();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal crash-modal">
        <div className="modal-header">
          <h2 className="modal-title">
            Crash Details
            {crash?.is_fatal && <span className="fatal-badge">FATAL</span>}
          </h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-content">
          {isLoading ? (
            <div className="modal-loading">Loading crash details...</div>
          ) : error ? (
            <div className="modal-error">{error}</div>
          ) : crash ? (
            <>
              {/* Crash Summary */}
              <div className="crash-summary">
                <div className="crash-message">{crash.message}</div>
                <div className="crash-meta">
                  <span>Device: {crash.device_id.slice(0, 12)}...</span>
                  <span>Time: {formatTimestamp(crash.timestamp)}</span>
                </div>
              </div>

              {/* Device State at Crash */}
              {crash.device_state && (
                <div className="crash-section">
                  <h3 className="crash-section-title">Device State at Crash</h3>
                  <div className="crash-device-state">
                    {crash.device_state.battery_level !== undefined && (
                      <div className="state-item">
                        <span className="state-label">Battery</span>
                        <span className="state-value">{crash.device_state.battery_level}%</span>
                      </div>
                    )}
                    {crash.device_state.memory_used_mb !== undefined && (
                      <div className="state-item">
                        <span className="state-label">Memory Used</span>
                        <span className="state-value">{crash.device_state.memory_used_mb} MB</span>
                      </div>
                    )}
                    {crash.device_state.is_doze_mode !== undefined && (
                      <div className="state-item">
                        <span className="state-label">Doze Mode</span>
                        <span className="state-value">{crash.device_state.is_doze_mode ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {crash.device_state.is_screen_on !== undefined && (
                      <div className="state-item">
                        <span className="state-label">Screen</span>
                        <span className="state-value">{crash.device_state.is_screen_on ? 'On' : 'Off'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stack Trace */}
              <div className="crash-section">
                <div className="crash-section-header">
                  <h3 className="crash-section-title">Stack Trace</h3>
                  <button
                    className="button button-small button-secondary"
                    onClick={() => setShowFullStack(!showFullStack)}
                  >
                    {showFullStack ? 'Show Less' : 'Show Full'}
                  </button>
                </div>
                <pre className={`crash-stack-trace ${showFullStack ? 'expanded' : ''}`}>
                  {showFullStack ? crash.stack_trace : crash.stack_trace.slice(0, 500) + (crash.stack_trace.length > 500 ? '...' : '')}
                </pre>
              </div>

              {/* Breadcrumbs */}
              {crash.breadcrumbs && crash.breadcrumbs.length > 0 && (
                <div className="crash-section">
                  <h3 className="crash-section-title">Breadcrumbs ({crash.breadcrumbs.length})</h3>
                  <div className="crash-breadcrumbs">
                    {crash.breadcrumbs.map((crumb, idx) => (
                      <div
                        key={idx}
                        className={`breadcrumb-item breadcrumb-${crumb.level || 'info'}`}
                      >
                        <span className="breadcrumb-time">
                          {new Date(crumb.timestamp).toLocaleTimeString()}
                        </span>
                        <span className="breadcrumb-category">[{crumb.category}]</span>
                        <span className="breadcrumb-message">{crumb.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Acknowledgement Status */}
              <div className="crash-section crash-acknowledgement">
                {crash.acknowledged ? (
                  <div className="acknowledged">
                    Acknowledged by {crash.acknowledged_by} at{' '}
                    {crash.acknowledged_at ? formatTimestamp(crash.acknowledged_at) : 'unknown'}
                  </div>
                ) : (
                  <div className="not-acknowledged">Not yet acknowledged</div>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="modal-footer">
          <button className="button button-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="button"
            onClick={() => {
              if (crash?.stack_trace) {
                navigator.clipboard.writeText(crash.stack_trace);
              }
            }}
          >
            Copy Stack Trace
          </button>
        </div>
      </div>
    </div>
  );
}
